import { Button, Col, Container, FormControl, FormLabel, Modal, Row } from "react-bootstrap";
import LoadingSpinner from "../Loading/LoadingSpinner";
import React, { useEffect, useRef, useState, useContext } from "react";
import { toast } from "react-toastify";
import messageFromError from "utils/errorUtils";
import { createGeofenceForCompany, getGeofenceExtraKeys } from "API/companyApi";
import useCompanyId from "../../Hooks/useCompanyId";
import ExtrasComponent from "../ExtrasComponent";
import { useTranslation } from "react-i18next";
import css from './css/RegisterGeofenceModal.module.css'
import ConditionalRender, { Fallback } from "../ConditionalRender";
import MapComponente from "../MapComponente";
import { SalvoContext } from "../../contexts/SalvoContext";

const RegisterGeofenceModal = ({ geofences, active, onRegister, onCancel }) => {

    const [showSpinner, setShowSpinner] = useState(false)
    const [points, setPoints] = useState([]);
    const [note, setNote] = useState('');
    const [name, setName] = useState('');
    // const [externalId, setExternalId] = useState('');
    const [projectNumber, setProjectNumber] = useState('');
    const [type, setType] = useState('customer');
    const [beginDate, setBeginDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const { geofenceProjectReference } = useContext(SalvoContext);

    const dateToBeginUtcIso = (dateStr) => (dateStr ? `${dateStr}T00:00:00.000Z` : null);
    const dateToEndUtcIso = (dateStr) => (dateStr ? `${dateStr}T23:59:59.000Z` : null);

    const companyId = useCompanyId();
    const { t } = useTranslation()

    const geofenceExtras = useRef([])

    useEffect(() => {
        if (!companyId)
            return

        fetchGeofenceExtraKeys()
    }, [companyId]);


    async function fetchGeofenceExtraKeys() {
        try {
            const response = await getGeofenceExtraKeys(companyId)
            geofenceExtras.current = response.data
        }
        catch (ex) {
            console.error(ex);
        }
    }

    async function register() {
        setShowSpinner(true)
        try {
            const geofence = {
                points: points,
                note: note,
                name: name,
                projectNumber: projectNumber,
                type: type,
                beginUtc: dateToBeginUtcIso(beginDate),
                endUtc: dateToEndUtcIso(endDate),
                extras: geofenceExtras.current
            }

            const response = await createGeofenceForCompany(companyId, geofence)
            setShowSpinner(false)
            setName('')
            // setExternalId('')
            setProjectNumber('')
            setNote('')
            setBeginDate('')
            setEndDate('')
            toast.success(t('dialogs.notifications.successfully') + t('dialogs.notifications.created'))
            onRegister(parseInt(response.data), name)
        }
        catch (error) {
            toast.error(messageFromError(error))
        }
        finally {
            setShowSpinner(false)
        }
    }

    return (
        <Modal centered show={active} onHide={() => { !showSpinner && onCancel() }} size='xl' contentClassName={css.modalHeight} dialogClassName={css.modalWidth}>
            <Modal.Header className='modal-header' >
                <Modal.Title>{t('pages.workLocations.registerGeofenceDialog.title')}</Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ display: 'flex', flexDirection: 'column' }}>
                <Container fluid className='mt-2'>
                    {/* Name and Project Reference (swapped with Note) */}
                    <Row className="mb-2">
                        {/* Name */}
                        <Col xs={6} className="d-flex align-items-center">
                            <FormLabel className="mb-0 me-2 flex-shrink-0" style={{ width: '160px' }}>
                                {t('pages.workLocations.registerGeofenceDialog.name')}:
                            </FormLabel>
                            <FormControl
                                className="flex-grow-1"
                                placeholder={t('dialogs.nameOptional')}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </Col>

                        {/* Project Reference (now top right, in Note's old spot/style) */}
                        <Col xs={6} className="d-flex align-items-center">
                            <FormLabel className="mb-0 me-2 flex-shrink-0" style={{ width: '160px' }}>
                                {t('pages.workLocations.projectNumber')}:
                            </FormLabel>
                            <FormControl
                                className="flex-grow-1"
                                placeholder="1234"
                                value={projectNumber}
                                onChange={(e) => setProjectNumber(e.target.value)}
                            />
                        </Col>
                    </Row>

                    <Row className="mb-2">
                        {/* Type */}
                        <Col xs={6} className="d-flex align-items-center">
                            <FormLabel className="mb-0 me-2 flex-shrink-0" style={{ width: '160px' }}>
                                {t('dialogs.type')}:
                            </FormLabel>
                            <FormControl as="select" className="flex-grow-1" value={type} onChange={(e) => setType(e.target.value)}>
                                <option value="site">{t('dialogs.site')}</option>
                                <option value="customer">{t('dialogs.customer')}</option>
                                <option value="supplier">{t('dialogs.supplier')}</option>
                                <option value="other">{t('dialogs.other')}</option>
                            </FormControl>
                        </Col>

                        {/* Note (now in Project Reference's old spot, longer input) */}
                        <Col xs={6} className="d-flex align-items-center">
                            <FormLabel className="mb-0 me-2 flex-shrink-0" style={{ width: '160px' }}>
                                {t('dialogs.note')}:
                            </FormLabel>
                            <FormControl
                                as="textarea"
                                rows={1}
                                className="flex-grow-1"
                                placeholder={t('dialogs.noteOptional')}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                style={{ maxHeight: "150px" }}
                            />
                        </Col>
                    </Row>

                    <Row className="mb-2">
                        {/* Begin Date (date-only, no time) */}
                        <Col xs={6} className="d-flex align-items-center">
                            <FormLabel className="mb-0 me-2 flex-shrink-0" style={{ width: '160px' }}>
                                {t('dialogs.beginTime')}:
                            </FormLabel>
                            <FormControl
                                type="date"
                                className="flex-grow-1"
                                value={beginDate}
                                onChange={(e) => setBeginDate(e.target.value)}
                                onClick={(e) => e.currentTarget.showPicker?.()}
                            />
                        </Col>

                        {/* End Date (date-only, no time) */}
                        <Col xs={6} className="d-flex align-items-center">
                            <FormLabel className="mb-0 me-2 flex-shrink-0" style={{ width: '160px' }}>
                                {t('dialogs.endTime')}:
                            </FormLabel>
                            <FormControl
                                type="date"
                                className="flex-grow-1"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                onClick={(e) => e.currentTarget.showPicker?.()}
                            />
                        </Col>

                        {/* Explanatory message */}
                        {(beginDate || endDate) && (
                            <Col xs={12}>
                                <small className="text-muted">{t("dialogs.timeExplanation")}</small>
                            </Col>
                        )}
                    </Row>

                    {/* Extras component */}
                    {geofenceProjectReference ?
                        <Row>
                            <ExtrasComponent extras={geofenceExtras.current} onChange={(extras) => geofenceExtras.current = extras} />
                        </Row> : null
                    }

                    <hr />
                </Container>

                <p style={{ fontSize: "18px", fontWeight: "bold" }}>
                    {t("pages.workLocations.registerGeofenceDialog.draw_work_location")}
                </p>

                <div style={{ flexGrow: 1, margin: '0 10px' }}>
                    <MapComponente
                        geofences={geofences}
                        containerStyle={{ position: 'relative', width: '100%', height: '100%', marginBottom: "20px" }}
                        onChange={(points) => setPoints(points)}
                    />
                </div>
            </Modal.Body>

            <Modal.Footer>
                <ConditionalRender condition={!showSpinner}>
                    <Fallback><LoadingSpinner variant='success' className='mx-4' /></Fallback>
                    <Button variant='danger' onClick={onCancel}>{t('global.operations.cancel')}</Button>
                    <Button disabled={points.length < 3} variant='success' onClick={register}>{t('global.operations.save')}</Button>
                </ConditionalRender>
            </Modal.Footer>
        </Modal>
    );
};

export default RegisterGeofenceModal;